export interface AutofillErrorItem {
    fieldLabel?: string;
    error?: string;
    appName?: string;
    retryAttempted?: boolean;
}

export interface ConfigurationWarningState {
    autofillErrors?: Array<AutofillErrorItem | string>;
    newlySharedFieldTitles?: string[];
    unsyncedLocations?: string[];
    posConfigErrors?: string[];
}

/**
 * Consolidates and formats warnings passed via router state after configuration updates.
 * Prevents toast lists from scaling linearly with the number of locations (e.g. 100 locations)
 * while providing clear, actionable summary messages.
 */
export const consolidateConfigurationWarnings = (state?: ConfigurationWarningState | null): string[] => {
    if (!state) return [];

    const warnings: string[] = [];

    // 1. Autofill Errors Consolidation
    if (state.autofillErrors && state.autofillErrors.length > 0) {
        const autofillMessage = formatAutofillWarnings(state.autofillErrors);
        if (autofillMessage) {
            warnings.push(autofillMessage);
        }
    }

    // 2. POS Configuration Errors Consolidation
    if (state.posConfigErrors && state.posConfigErrors.length > 0) {
        const posMessage = formatPosConfigWarnings(state.posConfigErrors);
        if (posMessage) {
            warnings.push(posMessage);
        }
    }

    // 3. Newly Shared Field Titles Notice
    if (state.newlySharedFieldTitles && state.newlySharedFieldTitles.length > 0) {
        const sharedFieldsMessage = formatNewlySharedFieldsNotice(state.newlySharedFieldTitles);
        if (sharedFieldsMessage) {
            warnings.push(sharedFieldsMessage);
        }
    }

    // 4. Unsynced Locations Warning
    if (state.unsyncedLocations && state.unsyncedLocations.length > 0) {
        const unsyncedMessage = formatUnsyncedLocationsWarning(state.unsyncedLocations);
        if (unsyncedMessage) {
            warnings.push(unsyncedMessage);
        }
    }

    return warnings;
};

/** Formats list of items with proper Oxford comma / conjunction */
export const formatListWithConjunction = (items: string[], conjunction: 'and' | 'or' = 'and'): string => {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items[items.length - 1]}`;
};

/** Consolidates autofill errors into a single concise, actionable message */
export const formatAutofillWarnings = (errors: Array<AutofillErrorItem | string>): string => {
    const normalized: Array<{ fieldLabel: string; appName?: string }> = errors
        .map(e => {
            if (typeof e === 'string') {
                return { fieldLabel: e.trim(), appName: undefined };
            }
            return {
                fieldLabel: (e.fieldLabel ?? '').trim(),
                appName: e.appName?.trim() || undefined
            };
        })
        .filter(e => e.fieldLabel.length > 0);

    if (normalized.length === 0) return '';

    const uniqueFields = Array.from(new Set(normalized.map(e => e.fieldLabel)));
    const uniqueApps = Array.from(new Set(normalized.map(e => e.appName).filter(Boolean))) as string[];

    // Case 1: Single location or shared-config-only autofill
    if (uniqueApps.length <= 1) {
        if (uniqueFields.length <= 3) {
            return `Autofill incomplete for ${formatListWithConjunction(uniqueFields)}. Please fill in manually.`;
        }
        return `Autofill incomplete for ${uniqueFields.length} fields (${uniqueFields.slice(0, 2).join(', ')}, and ${uniqueFields.length - 2} more). Please fill in manually.`;
    }

    // Case 2: 2 locations
    if (uniqueApps.length === 2) {
        if (uniqueFields.length <= 2) {
            return `Autofill incomplete for ${formatListWithConjunction(uniqueApps)} (${uniqueFields.join(', ')}). Please fill in manually.`;
        }
        return `Autofill incomplete for ${formatListWithConjunction(uniqueApps)} (${uniqueFields.length} fields). Please fill in manually.`;
    }

    // Case 3: 3+ locations (scales cleanly for 100+ locations)
    if (uniqueFields.length <= 2) {
        return `Autofill incomplete for ${uniqueApps.length} locations (${uniqueFields.join(', ')}). Please fill in manually.`;
    }
    return `Autofill incomplete for ${uniqueApps.length} locations across ${uniqueFields.length} fields. Please fill in manually.`;
};

/** Consolidates POS configuration errors into a concise summary */
export const formatPosConfigWarnings = (errors: string[]): string => {
    const validErrors = errors.map(e => (e ?? '').trim()).filter(e => e.length > 0);
    if (validErrors.length === 0) return '';

    if (validErrors.length === 1) {
        const error = validErrors[0];
        const match = error.match(/for "([^"]+)"/);
        const locName = match ? match[1] : '';

        if (error.includes('already has a POS configuration')) {
            return locName
                ? `POS configuration skipped for "${locName}" (already configured).`
                : 'POS configuration skipped (already configured).';
        }
        if (error.includes('clone source location does not have a POS configuration')) {
            return locName
                ? `POS configuration skipped for "${locName}" (no source configuration to clone).`
                : 'POS configuration skipped (no source configuration to clone).';
        }
        return locName
            ? `POS configuration could not be created for "${locName}".`
            : 'POS configuration could not be created.';
    }

    const alreadyConfiguredCount = validErrors.filter(e => e.includes('already has a POS configuration')).length;
    const noCloneSourceCount = validErrors.filter(e => e.includes('clone source location does not have a POS configuration')).length;
    const otherFailedCount = validErrors.length - alreadyConfiguredCount - noCloneSourceCount;

    if (alreadyConfiguredCount === validErrors.length) {
        return `POS configuration skipped for ${validErrors.length} locations (already configured).`;
    }

    if (noCloneSourceCount === validErrors.length) {
        return `POS configuration skipped for ${validErrors.length} locations (no source configuration to clone).`;
    }

    if (otherFailedCount === validErrors.length) {
        return `POS configuration could not be created for ${validErrors.length} locations.`;
    }

    // Mixed reasons: provide high-level concise breakdown
    const parts: string[] = [];
    if (alreadyConfiguredCount > 0) {
        parts.push(`${alreadyConfiguredCount} already configured`);
    }
    if (noCloneSourceCount > 0) {
        parts.push(`${noCloneSourceCount} without clone source`);
    }
    if (otherFailedCount > 0) {
        parts.push(`${otherFailedCount} failed`);
    }

    return `POS configuration skipped or failed for ${validErrors.length} locations (${parts.join(', ')}).`;
};

/** Consolidates newly shared field titles into a concise actionable notice */
export const formatNewlySharedFieldsNotice = (fieldTitles: string[]): string => {
    const validTitles = Array.from(new Set(fieldTitles.map(t => (t ?? '').trim()).filter(t => t.length > 0)));
    if (validTitles.length === 0) return '';

    if (validTitles.length <= 3) {
        const fields = formatListWithConjunction(validTitles);
        return `${fields} moved to Shared Configuration. Set values in Shared Config to apply across all locations.`;
    }

    return `${validTitles.length} fields moved to Shared Configuration (${validTitles.slice(0, 2).join(', ')}, etc.). Set values in Shared Config to apply across all locations.`;
};

/** Consolidates unsynced locations into a single concise notification */
export const formatUnsyncedLocationsWarning = (locationNames: string[]): string => {
    const validNames = Array.from(new Set(locationNames.map(n => (n ?? '').trim()).filter(n => n.length > 0)));
    if (validNames.length === 0) return '';

    if (validNames.length <= 2) {
        return `Configuration saved without syncing for ${formatListWithConjunction(validNames)}. Sync and publish from the dashboard to apply.`;
    }

    return `Configuration saved for ${validNames.length} locations without syncing. Sync and publish from the dashboard to apply.`;
};
